import { useState, useEffect } from 'react';
import MonitorSettings from './MonitorSettings';
import ProductInput from './ProductInput';
import MonitoredProductsList from './MonitoredProductsList';
import MonitorStatusIndicator from './MonitorStatusIndicator';
import MonitorControlPanel from './MonitorControlPanel';
import { getMonitorOverview } from '../utils/api';

interface MonitorDashboardProps {
  onStatsUpdate: () => void;
}

/**
 * 监控仪表板主组件
 */
const MonitorDashboard: React.FC<MonitorDashboardProps> = ({
  onStatsUpdate
}) => {
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // 加载监控概览数据
  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await getMonitorOverview();
      setOverview(data);
    } catch (error) {
              console.error('获取监控概览失败:', (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview();
  }, []);

  // 处理产品添加成功后的回调
  const handleProductAdded = () => {
    onStatsUpdate();
    loadOverview();
    setRefreshKey(prev => prev + 1); // 触发监控产品列表刷新
  };



  // 处理设置更新
  const handleSettingsUpdate = () => {
    onStatsUpdate();
    loadOverview();
  };

  // 处理监控完成回调
  const handleMonitorComplete = () => {
    console.log('[MonitorDashboard] 监控任务完成，刷新产品列表和统计数据');
    onStatsUpdate();
    loadOverview();
    setRefreshKey(prev => prev + 1); // 触发监控产品列表刷新
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* 页面标题 - 优化高度 */}
      <div className="bg-white/70 backdrop-blur-sm border-b border-gray-200/50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 bg-clip-text text-transparent">
              TikTok商品销量监控工具【贺总专用】
            </h1>
            <p className="text-lg text-gray-600 mt-2">
              实时监控TikTok商品销量趋势，通过数据可视化分析店铺表现，助力商业决策
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* 主要功能区域 - 网格布局 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* 左侧：添加监控产品 */}
          <ProductInput onProductAdded={handleProductAdded} />
          
          {/* 右侧：监控控制面板 */}
          <MonitorControlPanel 
            onStatusChange={() => {
              onStatsUpdate();
              loadOverview();
            }}
            onOpenSettings={() => setIsSettingsOpen(true)}
          />
        </div>

        {/* 监控统计信息 */}
        <div className="mb-6">
          <div className="card bg-base-100 shadow-lg">
            <div className="card-body">
              <div className="flex justify-between items-center mb-4">
                <h3 className="card-title text-lg">
                  <span className="text-info">📊</span>
                  监控统计
                </h3>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    onStatsUpdate();
                    loadOverview();
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                  )}
                </button>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <span className="loading loading-spinner loading-md text-primary"></span>
                  <span className="ml-3 text-base-content/60">加载中...</span>
                </div>
              ) : overview ? (
                <div className="space-y-4">
                  {/* 基础统计 - 横向布局 */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="stat bg-gradient-to-r from-blue-500/10 to-blue-600/10 rounded-lg p-3">
                      <div className="stat-title text-xs">监控产品</div>
                      <div className="stat-value text-xl text-blue-600">
                        {(overview.total_monitored_products || 0).toLocaleString('zh-CN')}
                      </div>
                      <div className="stat-desc text-xs">个产品</div>
                    </div>
                    
                    <div className="stat bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg p-3">
                      <div className="stat-title text-xs">监控记录</div>
                      <div className="stat-value text-xl text-green-600">
                        {(overview.total_monitor_records || 0).toLocaleString('zh-CN')}
                      </div>
                      <div className="stat-desc text-xs">条记录</div>
                    </div>

                    {/* 今日监控次数 */}
                        <div className="stat bg-gradient-to-r from-purple-500/10 to-purple-600/10 rounded-lg p-3">
                          <div className="stat-title text-xs">今日监控次数</div>
                          <div className="stat-value text-xl text-purple-600">
                            {(overview.today_stats?.monitor_count || 0).toLocaleString('zh-CN')}
                          </div>
                          <div className="stat-desc text-xs">次</div>
                        </div>
                        
                    {/* 自动监控状态指示器 */}
                    <MonitorStatusIndicator onMonitorComplete={handleMonitorComplete} />
                  </div>


                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">📈</div>
                  <p className="text-base-content/60">暂无统计数据</p>
                  <p className="text-sm text-base-content/40 mt-1">
                    添加监控产品后开始记录数据
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 监控产品列表 */}
        <div className="mb-6">
          <MonitoredProductsList 
            key={refreshKey}
          />
        </div>






      </div>

      {/* 监控设置模态框 */}
      <MonitorSettings 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onSettingsUpdate={handleSettingsUpdate}
      />
    </div>
  );
};

export default MonitorDashboard; 